// src/BuildTrackerDashboard.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './BuildTrackerDashboard.css';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || ''
});

const BuildTrackerDashboard = ({ customerEmail, onEmailChange }) => {
  const [orders, setOrders] = useState([]);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);

  const stateInfo = {
    1: {
      title: "Order Placed",
      description: "We've received your order and are making preparations. If any parts need to be ordered, we'll do that first — otherwise expect your lights to be opened soon.",
      icon: "📦",
      color: "#ff6b6b"
    },
    2: {
      title: "Lights Opened", 
      description: "Your lights have been cooked open and are being prepped for paint.",
      icon: "🔥",
      color: "#ff8c42"
    },
    3: {
      title: "Paint Complete",
      description: "Your lights have fully cured and are ready for retrofitting.",
      icon: "🎨",
      color: "#ffd23f"
    },
    4: {
      title: "Retrofit Complete",
      description: "All components are secured & tested and headlights are sealed. Next we complete the wiring harness and flood test the seal.",
      icon: "🔧",
      color: "#06d6a0"
    },
    5: {
      title: "Seal Test Passed, Pending Shipment",
      description: "Your headlights are almost ready! We're just packaging them up and doing a final check!",
      icon: "✅",
      color: "#118ab2"
    },
    6: {
      title: "Order Shipped",
      description: "Your order is on its way! Check your tracking information and thank you for choosing DOCTORdripp!",
      icon: "🚚",
      color: "#06d6a0"
    }
  };

  useEffect(() => {
    if (customerEmail || emailSubmitted) {
      fetchOrders();
    }
  }, [customerEmail, emailSubmitted]);

  const fetchOrders = async () => {
    const emailToUse = customerEmail || emailInput;
    if (!emailToUse) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/api/build-tracker?action=orders&email=${encodeURIComponent(emailToUse)}`);
      
      if (response.data.success) {
        setOrders(response.data.orders);
        if (response.data.orders.length > 0) {
          setSelectedOrder(response.data.orders[0]);
        }
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrderDetail = async (orderId) => {
    const emailToUse = customerEmail || emailInput;
    try {
      setDetailLoading(true);
      const response = await api.get(`/api/build-tracker?action=order-detail&order_id=${orderId}&customer_email=${encodeURIComponent(emailToUse)}`);
      
      if (response.data.success) {
        setSelectedOrder(response.data.order);
      }
    } catch (err) {
      console.error('Error fetching order detail:', err);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleOrderSelect = (order) => {
    setSelectedOrder(order);
    fetchOrderDetail(order.id);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (emailInput.trim()) {
      setEmailSubmitted(true);
      // Update the parent component's email state for the floating header
      if (onEmailChange) {
        onEmailChange(emailInput.trim());
      }
    }
  };

  const handleEmailKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleEmailSubmit(e);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Pending';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const getProgressPercentage = (state) => {
    if (state <= 1) return 0;
    if (state >= 6) return 100;
    return ((state - 1) / 5) * 100;
  };

  // Show email form if no email is provided
  if (!customerEmail && !emailSubmitted) {
    return (
      <div className="build-tracker-dashboard">
        <div className="email-form-container">
          <h3>BUILD PROGRESS</h3>
          <p>Enter your email to track custom build orders</p>
          <form onSubmit={handleEmailSubmit} className="email-form">
            <div className="email-input-group">
              <input
                type="email"
                value={emailInput}
                onChange={(e) => setEmailInput(e.target.value)}
                onKeyPress={handleEmailKeyPress}
                placeholder="Enter your email address"
                className="email-input"
                required
              />
              <button type="submit" className="submit-button">
                Track Orders
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="build-tracker-dashboard">
        <div className="loading-container">
          <div className="build-tracker-loading-spinner"></div>
          <p>Loading your build progress...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="build-tracker-dashboard">
        <div className="error-container">
          <h3>Error Loading Build Tracker</h3>
          <p>{error}</p>
          <button onClick={fetchOrders} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (orders.length === 0) {
    return (
      <div className="build-tracker-dashboard">
        <div className="no-orders-container">
          <h3>No Custom Build Orders Found</h3>
          <p>You don't have any custom build orders to track yet.</p>
          <p>When you place an order for custom-built products, you'll be able to track the build progress here!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="build-tracker-dashboard">
      <div className="dashboard-header">
        <h2>Build Tracker Dashboard</h2>
        <p>Track the progress of your custom-built orders</p>
      </div>

      <div className="dashboard-content">
        {/* Orders List */}
        <div className="orders-sidebar">
          <h3>Your Orders</h3>
          <div className="orders-list">
            {orders.map((order) => (
              <div
                key={order.id}
                className={`order-card ${selectedOrder?.id === order.id ? 'active' : ''}`}
                onClick={() => handleOrderSelect(order)}
              >
                <div className="order-header">
                  <span className="order-number">#{order.order_number}</span>
                  <span className={`state-badge state-${order.current_state}`}>
                    {stateInfo[order.current_state]?.icon} {stateInfo[order.current_state]?.title}
                  </span>
                </div>
                <div className="order-meta">
                  <span className="order-date">{formatDate(order.order_date)}</span>
                  <span className="order-total">{formatCurrency(order.order_total)}</span>
                </div>
                <div className="order-progress">
                  <div className="progress-bar-mini">
                    <div 
                      className="progress-fill-mini"
                      style={{ 
                        width: `${getProgressPercentage(order.current_state)}%`,
                        backgroundColor: stateInfo[order.current_state]?.color
                      }}
                    />
                  </div>
                  <span className="progress-text-mini">
                    {getProgressPercentage(order.current_state)}% Complete
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Order Detail */}
        <div className="order-detail">
          {selectedOrder ? (
            <div className="detail-content">
              {detailLoading && (
                <div className="detail-loading">
                  <div className="build-tracker-loading-spinner small"></div>
                </div>
              )}
              
              <div className="detail-header">
                <h3>Order #{selectedOrder.order_number}</h3>
                <div className="detail-meta">
                  <span>Placed: {formatDate(selectedOrder.order_date)}</span>
                  <span>Total: {formatCurrency(selectedOrder.order_total)}</span>
                  <span>Days in Progress: {selectedOrder.days_in_progress}</span>
                </div>
              </div>

              {/* Main Progress Display */}
              <div className="main-progress">
                <div className="progress-title">
                  <h3>BUILD PROGRESS</h3>
                </div>
                <div className="current-state">
                  <div className="state-icon" style={{ color: stateInfo[selectedOrder.current_state]?.color }}>
                    {stateInfo[selectedOrder.current_state]?.icon}
                  </div>
                  <div className="state-info">
                    <h4 className="state-title-rainbow">{stateInfo[selectedOrder.current_state]?.title}</h4>
                    <p>{stateInfo[selectedOrder.current_state]?.description}</p>
                  </div>
                </div>
                
                <div className="progress-bar-main">
                  <div 
                    className="progress-fill-main"
                    style={{ 
                      width: `${getProgressPercentage(selectedOrder.current_state)}%`,
                      background: `linear-gradient(90deg, ${stateInfo[selectedOrder.current_state]?.color}, ${stateInfo[selectedOrder.current_state]?.color}90)`
                    }}
                  />
                </div>
                
                <div className="progress-percentage">
                  {getProgressPercentage(selectedOrder.current_state)}% Complete
                </div>
              </div>

              {/* State Timeline */}
              <div className="state-timeline">
                <h4>Progress Timeline</h4>
                <div className="timeline">
                  {Object.entries(stateInfo).map(([stateNum, info]) => {
                    const stateNumber = parseInt(stateNum);
                    const isCompleted = selectedOrder.current_state >= stateNumber;
                    const isCurrent = selectedOrder.current_state === stateNumber;
                    
                    // Get the actual timestamp for this state
                    const stateTimestampField = `state_${stateNumber}_at`;
                    const stateTimestamp = selectedOrder[stateTimestampField];
                    
                    let displayDate;
                    if (isCompleted && stateTimestamp) {
                      displayDate = new Date(stateTimestamp).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }).replace(',', ' at');
                    } else if (isCompleted && stateNumber === 1) {
                      // Fallback to order date for state 1 if no specific timestamp
                      displayDate = new Date(selectedOrder.order_date).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      }).replace(',', ' at');
                    } else if (isCurrent) {
                      displayDate = 'In Progress';
                    } else {
                      displayDate = 'Pending';
                    }
                    
                    return (
                      <div
                        key={stateNum}
                        className={`timeline-item ${isCompleted ? 'completed' : ''} ${isCurrent ? 'current' : ''}`}
                      >
                        <div className="timeline-marker" style={{ backgroundColor: isCompleted ? info.color : '#ccc' }}>
                          {info.icon}
                        </div>
                        <div className="timeline-content">
                          <h5>{info.title}</h5>
                          <p className="timeline-date">
                            {displayDate}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Order Items */}
              <div className="order-items">
                <h4>Order Items</h4>
                <div className="items-list">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="item-card">
                      <div className="item-info">
                        <h5>{item.product_title || item.title}</h5>
                        {(item.variant_title || item.variant) && <p className="item-variant">{item.variant_title || item.variant}</p>}
                        <div className="item-meta">
                          <span>Qty: {item.quantity}</span>
                          <span>{formatCurrency(item.price)}</span>
                          {item.is_custom_built && (
                            <span className="custom-built-badge">🛠️ Custom Built</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Tracking Information */}
              {selectedOrder.tracking_number && (
                <div className="tracking-info">
                  <h4>Shipping Information</h4>
                  <div className="tracking-details">
                    <p><strong>Tracking Number:</strong> {selectedOrder.tracking_number}</p>
                    <p><strong>Status:</strong> {selectedOrder.fulfillment_status}</p>
                  </div>
                </div>
              )}

              {/* Manual Override Info */}
              {/* Manual override info removed - customers don't need to see admin updates */}
            </div>
          ) : (
            <div className="no-selection">
              <p>Select an order to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BuildTrackerDashboard;
